import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';

import {
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  SignalProperties,
  VentilationBypassModeCommands,
  VentilationBypassModeProperties,
  VentilationBypassPositionProperties,
  VentilationModeCommands,
  VentilationModeProperties,
  VentilationOperatingPrograms,
  VentilationOperatingReasons,
  VitotrolRemoteControllerCommands,
  VitotrolRemoteControllerProperties,
  VentilationStateProperties,
} from '@vi-iot/client';

import { DevicesStore, InstallationFeaturesStore, RichDevice } from '@store';

import { lowLqiSignalThreshold } from '@common';
import { switchMap, takeUntil } from 'rxjs/operators';
import { FeatureTileIconVariant } from 'src/app/shared/components/feature-tile/feature-tile-variants';
import { FeaturesDetailsOverlayTypeEnum } from '../installation-overlay.model';

@Component({
  selector: 'app-ventilation',
  templateUrl: './ventilation.component.html',
  styleUrls: ['./ventilation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationComponent implements OnInit, OnDestroy {
  @Input() deviceSelectId: string;
  @Input() deviceId: string;

  readonly featurePropertyKey = FeaturePropertyKey;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly iconVariant = FeatureTileIconVariant;

  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  VentilationOperatingPrograms = VentilationOperatingPrograms;

  ventilationState$: Observable<Feature<VentilationStateProperties, never>>;
  ventilationMode$: Observable<Feature<VentilationModeProperties, VentilationModeCommands>>;
  ventilationBypassMode$: Observable<
    Feature<VentilationBypassModeProperties, VentilationBypassModeCommands>
  >;
  ventilationBypassPosition$: Observable<Feature<VentilationBypassPositionProperties, never>>;
  ventilationRemoteController$: Observable<
    Feature<VitotrolRemoteControllerProperties, VitotrolRemoteControllerCommands>
  >;

  remoteControllerDevices: RichDevice[];

  listReasonToShowLevel: string[] = [
    VentilationOperatingReasons.schedule,
    VentilationOperatingReasons.sensorDriven,
    VentilationOperatingReasons.sensorOverride,
    VentilationOperatingReasons.permanent,
  ];

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public devicesStore: DevicesStore,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.ventilationMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationMode,
      this.deviceId
    );

    this.ventilationBypassMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationBypassMode,
      this.deviceId
    );

    this.ventilationBypassPosition$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationBypassPosition,
      this.deviceId
    );

    this.ventilationState$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationState,
      this.deviceId
    );

    this.assignRemoteControllerDevices();
  }

  getLqiFeature(deviceId: string): Observable<Feature<SignalProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceZigbeeLqi,
      deviceId
    );
  }

  getDeviceDescription(
    deviceId: string
  ): Observable<Feature<DeviceNameProperties, DeviceNameCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, deviceId);
  }

  private assignRemoteControllerDevices(): void {
    this.ventilationRemoteController$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationRemoteController
    );

    this.ventilationRemoteController$
      .pipe(
        switchMap((remoteController) => {
          if (remoteController?.properties?.deviceId?.value) {
            return this.devicesStore.getRemoteControllerDevices$;
          } else {
            return of(undefined);
          }
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((devices: RichDevice[]) => {
        this.remoteControllerDevices = devices;
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
